import { useEffect, useMemo, useState } from "react";
import { Check, Plus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AlterationLayout from "./AlterationLayout";
import { listenToWorkflowCollection } from "../../services/workflowCatalogService";
import { readAlterationDraft, updateAlterationDraft } from "../../lib/alterationDraft";

const ALTERATION_STARTING_PRICE = 99;

export default function AlterationServicesPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const existing = readAlterationDraft();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set((existing.services || []).map((item) => item.id)));

  useEffect(() => listenToWorkflowCollection("garments", (garments) => {
    const isCategoryId = garments.some((garment) => [garment.sectionId, garment.stitchingCategoryId, garment.categoryId].map(String).includes(String(groupId)));
    if (isCategoryId) navigate(`/services/alterations/${groupId}/garments`, { replace: true });
  }, () => {}), [groupId, navigate]);

  useEffect(() => listenToWorkflowCollection("alteration_services", (data) => {
    setItems(data.filter((item) => [item.categoryId, item.sectionId, item.garmentId].map(String).includes(String(groupId))));
    setLoading(false);
  }, () => setLoading(false)), [groupId]);

  const chosen = useMemo(() => items.filter((item) => selected.has(item.id)), [items, selected]);
  const next = () => {
    updateAlterationDraft({
      garmentId: groupId,
      services: chosen.map((item) => ({
        id: item.id,
        name: item.name || item.serviceName,
        description: item.description || "",
        requiresMeasurements: item.requiresMeasurements === true,
        requiresGarmentPhotos: item.requiresGarmentPhotos === true,
        pricingPolicy: item.pricingPolicy || {},
        minimumPrice: ALTERATION_STARTING_PRICE,
        maximumPrice: ALTERATION_STARTING_PRICE,
      })),
    });
    navigate(`/services/alterations/${groupId}/details`);
  };

  const backPath = existing.categoryId ? `/services/alterations/${existing.categoryId}/garments` : "/services/alterations";

  return <AlterationLayout back={() => navigate(backPath)} title="Choose alteration services" intro="Select every alteration or additional service needed for this garment.">
    <div className="mt-10 space-y-4">
      {loading ? <div className="card-soft p-8">Loading services…</div> : !items.length ? <div className="card-soft p-8 text-center"><h2 className="font-display text-2xl">No alteration services available</h2><p className="text-sm text-[#5b5a56] mt-2">Services assigned to this garment in the admin panel will appear here.</p></div> : items.map((item) => {
        const active = selected.has(item.id);
        return <button key={item.id} onClick={() => setSelected((old) => { const nextSet = new Set(old); nextSet.has(item.id) ? nextSet.delete(item.id) : nextSet.add(item.id); return nextSet; })} className={`card-soft w-full p-6 flex items-center justify-between text-left border-2 ${active ? "border-[#F46524]" : "border-transparent"}`}>
          <div><h2 className="font-display text-2xl">{item.name || item.serviceName}</h2><p className="text-sm text-[#5b5a56] mt-2">{item.description}</p><p className="text-sm font-semibold text-[#F46524] mt-2">Starts from Rs.99</p></div>
          <span className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? "bg-[#F46524] text-white" : "bg-[#FFEDDD] text-[#F46524]"}`}>{active ? <Check /> : <Plus />}</span>
        </button>;
      })}
    </div>
    {chosen.length ? <div className="card-soft p-6 mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"><strong>{chosen.length} service{chosen.length === 1 ? "" : "s"} selected</strong><button onClick={next} className="btn-primary justify-center">Review selection</button></div> : null}
  </AlterationLayout>;
}
